import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Server, Service } from '@flashvpn-io/web-core';
import { MessageAction, MessageService } from './message.service';
import { ToastService } from '../components/ui/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
export type ConnectionStatus = 'connected' | 'disconnected';

@Injectable({
    providedIn: 'root'
})
export class StatusService {
    // 连接状态
    private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>('disconnected');
    private modeSubject = new BehaviorSubject<'global' | 'rules'>('rules');
    private selectedServerSubject = new BehaviorSubject<Server | null>(null);
    private serversSubject = new BehaviorSubject<Server[]>([]);
    private serviceSubject = new BehaviorSubject<Service | null>(null);


    constructor(
        private messageService: MessageService,
        private toastService: ToastService,
        private translate: TranslateService
    ) {
        this.loadStateFromLocalStorage();
    }

    getConnectionStatus(): Observable<ConnectionStatus> {
        return this.connectionStatusSubject.asObservable();
    }

    getCurrentConnectionStatus(): ConnectionStatus {
        return this.connectionStatusSubject.getValue();
    }

    setService(service: Service): void {
        this.serviceSubject.next(service);
        localStorage.setItem('service', JSON.stringify(service));
    }

    getService(): Observable<Service | null> {
        return this.serviceSubject.asObservable();
    }

    getCurrentService(): Service | null {
        return this.serviceSubject.getValue();
    }

    setConnectionStatus(status: ConnectionStatus): void {
        this.connectionStatusSubject.next(status);
        localStorage.setItem('connectionStatus', status);
    }

    getMode(): Observable<'global' | 'rules'> {
        return this.modeSubject.asObservable();
    }

    getCurrentMode(): 'global' | 'rules' {
        return this.modeSubject.getValue();
    }

    setMode(mode: 'global' | 'rules'): void {
        this.turnOffProxy();
        this.modeSubject.next(mode);
        this.turnOnProxy();
        localStorage.setItem('proxyMode', mode);
    }

    toggleStatus(): void {
        const status = this.getCurrentConnectionStatus();
        if (status === 'disconnected') {
            console.log(this.serviceCheck());
            if (this.serviceCheck()) {
                this.toastService.error(this.translate.instant('Common.ServiceIsOverused'), 4000);
                return;
            }
            this.turnOnProxy();
        } else {
            this.turnOffProxy();
        }
    }

    serviceCheck() {
        const service = this.getCurrentService();
        return service && service.overUsed();
    }

    private turnOnProxy() {
        let server = this.getCurrentSelectedServer();
        if (!server) {
            server = this.getCurrentServers()[0];
        }
        const config = this.serverToConfig(server);
        this.messageService.sendMessage(MessageAction.TURN_ON_PROXY, config);
        this.setConnectionStatus('connected');
    }

    private turnOffProxy() {
        this.messageService.sendMessage(MessageAction.TURN_OFF_PROXY, null);
        this.setConnectionStatus('disconnected');
    }

    serverToConfig(server: Server): ProxyConfig {
        return {
            host: server.host,
            port: server.port,
            username: server.username,
            password: server.password,
            mode: this.getCurrentMode(),
            bypassList: this.getBypassList(),
        }
    }

    getSelectedServer(): Observable<Server | null> {
        return this.selectedServerSubject.asObservable();
    }

    getCurrentSelectedServer(): Server | null {
        return this.selectedServerSubject.getValue();
    }

    setCurrentServer(server: Server): void {
        this.selectedServerSubject.next(server);
        localStorage.setItem('selectedServerId', `${server.id}`);
        const status = this.getCurrentConnectionStatus();
        if (status === 'connected') {
            this.turnOnProxy();
            setTimeout(() => {
                this.turnOnProxy();
            }, 300);
        }
    }

    getServers(): Observable<Server[]> {
        return this.serversSubject.asObservable();
    }


    getCurrentServers() {
        return this.serversSubject.getValue();
    }

    getServersFromLocalStorage(): Server[] {
        const servers = localStorage.getItem('servers');
        return servers ? JSON.parse(servers) : [];
    }

    setServers(servers: Server[]): void {
        localStorage.setItem('servers', JSON.stringify(servers));
        this.serversSubject.next(servers);
    }

    clearCache(): void {
        this.turnOffProxy();
        localStorage.clear();
    }

    private loadStateFromLocalStorage(): void {
        // 加载连接状态
        const savedStatus = localStorage.getItem('connectionStatus');
        if (savedStatus) {
            this.connectionStatusSubject.next(savedStatus as ConnectionStatus);
        }

        // 加载代理模式
        const savedMode = localStorage.getItem('proxyMode');
        if (savedMode) {
            this.modeSubject.next(savedMode as 'global' | 'rules');
        }

        // 加载已保存的服务器
        const servers = this.getServersFromLocalStorage();
        if (servers.length > 0) {
            this.serversSubject.next(servers);
        }

        // 加载当前选中的服务器
        const savedServerId = localStorage.getItem('selectedServerId');
        if (savedServerId && servers.length > 0) {
            const selectedServer = servers.find(server => server.id === +savedServerId);
            if (selectedServer) {
                const selectedServer = servers.find(server => server.id === +savedServerId);
                this.selectedServerSubject.next(selectedServer as Server);
            }
        }
    }

    getBypassList(): string[] {
        return [
            '*.ip.sb',
            '*.flare.today',
            '*.googletraveladservices.com',
            '*.dl.google.com',
            '*.mtalk.google.com',
            '*.livew.l.qq.com',
            '*.vd.l.qq.com',
            '*.analytics.strava.com',
            '*.gspe1-ssl.ls.apple.com',
            '*.qhres.com',
            '*.qhimg.com',
            '*.akadns.net',
            '*.akamai.net',
            '*.akamaiedge.net',
            '*.akamaihd.net',
            '*.akamaistream.net',
            '*.akamaized.net',
            '*.%E4%BC%98%E9%85%B7*',
            '*.alibaba.com',
            '*.alicdn.com',
            '*.alikunlun.com',
            '*.alipay.com',
            '*.amap.com',
            '*.autonavi.com',
            '*.dingtalk.com',
            '*.mxhichina.com',
            '*.soku.com',
            '*.taobao.com',
            '*.tmall.com',
            '*.tmall.hk',
            '*.ykimg.com',
            '*.youku.com',
            '*.xiami.com',
            '*.xiami.net',
            '*.aaplimg.com',
            '*.apple.co',
            '*.apple.com',
            '*.apple.com.cn',
            '*.apple-cloudkit.com',
            '*.appstore.com',
            '*.cdn-apple.com',
            '*.crashlytics.com',
            '*.icloud.com',
            '*.icloud.com.cn',
            '*.icloud-content.com',
            '*.me.com',
            '*.mzstatic.com',
            '*.www-cdn.icloud.com.akadns.net',
            '*.baidu.com',
            '*.baidubcr.com',
            '*.bdstatic.com',
            '*.yunjiasu-cdn.net',
            '*.acgvideo.com',
            '*.biliapi.com',
            '*.biliapi.net',
            '*.bilibili.com',
            '*.bilibili.tv',
            '*.hdslb.com',
            '*.blizzard.com',
            '*.battle.net',
            '*.blzddist1-a.akamaihd.net',
            '*.feiliao.com',
            '*.pstatp.com',
            '*.snssdk.com',
            '*.iesdouyin.com',
            '*.toutiao.com',
            '*.cctv.com',
            '*.cctvpic.com',
            '*.livechina.com',
            '*.didialift.com',
            '*.didiglobal.com',
            '*.udache.com',
            '*.343480.com',
            '*.baduziyuan.com',
            '*.com-hs-hkdy.com',
            '*.czybjz.com',
            '*.dandanzan.com',
            '*.fjhps.com',
            '*.kuyunbo.club',
            '*.21cn.com',
            '*.hitv.com',
            '*.mgtv.com',
            '*.iqiyi.com',
            '*.iqiyipic.com',
            '*.71.am.com',
            '*.jd.com',
            '*.jd.hk',
            '*.jdpay.com',
            '*.360buyimg.com',
            '*.iciba.com',
            '*.ksosoft.com',
            '*.meitu.com',
            '*.meitudata.com',
            '*.meitustat.com',
            '*.meipai.com',
            '*.duokan.com',
            '*.mi-img.com',
            '*.miui.com',
            '*.miwifi.com',
            '*.xiaomi.com',
            '*.microsoft.com',
            '*.msecnd.net',
            '*.office365.com',
            '*.outlook.com',
            '*.s-microsoft.com',
            '*.visualstudio.com',
            '*.windows.com',
            '*.windowsupdate.com',
            '*.officecdn-microsoft-com.akamaized.net',
            '*.163.com',
            '*.126.net',
            '*.127.net',
            '*.163yun.com',
            '*.lofter.com',
            '*.netease.com',
            '*.ydstatic.com',
            '*.sina.com',
            '*.weibo.com',
            '*.weibocdn.com',
            '*.sohu.com',
            '*.sohucs.com',
            '*.sohu-inc.com',
            '*.v-56.com',
            '*.sogo.com',
            '*.sogou.com',
            '*.sogoucdn.com',
            '*.steampowered.com',
            '*.steam-chat.com',
            '*.steamgames.com',
            '*.steamusercontent.com',
            '*.steamcontent.com',
            '*.steamstatic.com',
            '*.steamcdn-a.akamaihd.net',
            '*.steamstat.us',
            '*.gtimg.com',
            '*.idqqimg.com',
            '*.igamecj.com',
            '*.myapp.com',
            '*.myqcloud.com',
            '*.qq.com',
            '*.servicewechat.com',
            '*.tencent.com',
            '*.tencent-cloud.net',
            '*.tenpay.com',
            '*.file-igamecj.akamaized.net',
            '*.YYeTs*',
            '*.jstucdn.com',
            '*.zimuzu.io',
            '*.zimuzu.tv',
            '*.zmz2019.com',
            '*.zmzapi.com',
            '*.zmzapi.net',
            '*.zmzfile.com',
            '*.ccgslb.com',
            '*.ccgslb.net',
            '*.chinanetcenter.com',
            '*.meixincdn.com',
            '*.ourdvs.com',
            '*.staticdn.net',
            '*.wangsu.com',
            '*.ipip.net',
            '*.ip.la',
            '*.ip-cdn.com',
            '*.ipv6-test.com',
            '*.test-ipv6.com',
            '*.whatismyip.com',
            '*.speedtest.net',
            '*.netspeedtestmaster.com',
            '*.speedtest.macpaw.com',
            '*.awesome-hd.me',
            '*.broadcasthe.net',
            '*.chdbits.co',
            '*.classix-unlimited.co.uk',
            '*.empornium.me',
            '*.gazellegames.net',
            '*.hdchina.org',
            '*.hdsky.me',
            '*.icetorrent.org',
            '*.jpopsuki.eu',
            '*.keepfrds.com',
            '*.madsrevolution.net',
            '*.m-team.cc',
            '*.nanyangpt.com',
            '*.ncore.cc',
            '*.open.cd',
            '*.ourbits.club',
            '*.passthepopcorn.me',
            '*.privatehd.to',
            '*.redacted.ch',
            '*.springsunday.net',
            '*.tjupt.org',
            '*.totheglory.im',
            '*.acm.org',
            '*.acs.org',
            '*.aip.org',
            '*.ams.org',
            '*.annualreviews.org',
            '*.aps.org',
            '*.ascelibrary.org',
            '*.asm.org',
            '*.asme.org',
            '*.astm.org',
            '*.bmj.com',
            '*.cambridge.org',
            '*.cas.org',
            '*.clarivate.com',
            '*.ebscohost.com',
            '*.emerald.com',
            '*.engineeringvillage.com',
            '*.icevirtuallibrary.com',
            '*.ieee.org',
            '*.imf.org',
            '*.iop.org',
            '*.jamanetwork.com',
            '*.jhu.edu',
            '*.jstor.org',
            '*.karger.com',
            '*.libguides.com',
            '*.madsrevolution.net',
            '*.mpg.de',
            '*.myilibrary.com',
            '*.nature.com',
            '*.oecd-ilibrary.org',
            '*.osapublishing.org',
            '*.oup.com',
            '*.ovid.com',
            '*.oxfordartonline.com',
            '*.oxfordbibliographies.com',
            '*.oxfordmusiconline.com',
            '*.pnas.org',
            '*.proquest.com',
            '*.rsc.org',
            '*.sagepub.com',
            '*.sciencedirect.com',
            '*.sciencemag.org',
            '*.scopus.com',
            '*.siam.org',
            '*.spiedigitallibrary.org',
            '*.springer.com',
            '*.springerlink.com',
            '*.tandfonline.com',
            '*.un.org',
            '*.uni-bielefeld.de',
            '*.webofknowledge.com',
            '*.westlaw.com',
            '*.wiley.com',
            '*.worldbank.org',
            '*.worldscientific.com',
            '*.cn',
            '*.360in.com',
            '*.51ym.me',
            '*.8686c.com',
            '*.abchina.com',
            '*.accuweather.com',
            '*.aicoinstorge.com',
            '*.air-matters.com',
            '*.air-matters.io',
            '*.aixifan.com',
            '*.amd.com',
            '*.b612.net',
            '*.bdatu.com',
            '*.beitaichufang.com',
            '*.bjango.com',
            '*.booking.com',
            '*.bstatic.com',
            '*.cailianpress.com',
            '*.camera360.com',
            '*.chinaso.com',
            '*.chua.pro',
            '*.chuimg.com',
            '*.chunyu.mobi',
            '*.chushou.tv',
            '*.cmbchina.com',
            '*.cmbimg.com',
            '*.ctrip.com',
            '*.dfcfw.com',
            '*.docschina.org',
            '*.douban.com',
            '*.doubanio.com',
            '*.douyu.com',
            '*.dxycdn.com',
            '*.dytt8.net',
            '*.eastmoney.com',
            '*.eudic.net',
            '*.feng.com',
            '*.fengkongcloud.com',
            '*.frdic.com',
            '*.futu5.com',
            '*.futunn.com',
            '*.gandi.net',
            '*.geilicdn.com',
            '*.getpricetag.com',
            '*.gifshow.com',
            '*.godic.net',
            '*.hicloud.com',
            '*.hongxiu.com',
            '*.hostbuf.com',
            '*.huxiucdn.com',
            '*.huya.com',
            '*.infinitynewtab.com',
            '*.ithome.com',
            '*.java.com',
            '*.jidian.im',
            '*.kaiyanapp.com',
            '*.kaspersky-labs.com',
            '*.keepcdn.com',
            '*.kkmh.com',
            '*.licdn.com',
            '*.linkedin.com',
            '*.loli.net',
            '*.luojilab.com',
            '*.maoyan.com',
            '*.maoyun.tv',
            '*.meituan.com',
            '*.meituan.net',
            '*.mobike.com',
            '*.moke.com',
            '*.mubu.com',
            '*.myzaker.com',
            '*.nim-lang-cn.org',
            '*.nvidia.com',
            '*.oracle.com',
            '*.paypal.com',
            '*.paypalobjects.com',
            '*.qdaily.com',
            '*.qidian.com',
            '*.qyer.com',
            '*.qyerstatic.com',
            '*.raychase.net',
            '*.ronghub.com',
            '*.ruguoapp.com',
            '*.s-reader.com',
            '*.sankuai.com',
            '*.scomper.me',
            '*.seafile.com',
            '*.sm.ms',
            '*.smzdm.com',
            '*.snapdrop.net',
            '*.snwx.com',
            '*.sspai.com',
            '*.takungpao.com',
            '*.teamviewer.com',
            '*.tianyancha.com',
            '*.udacity.com',
            '*.uning.com',
            '*.vmware.com',
            '*.weather.com',
            '*.weico.cc',
            '*.weidian.com',
            '*.xiachufang.com',
            '*.ximalaya.com',
            '*.xinhuanet.com',
            '*.xmcdn.com',
            '*.yangkeduo.com',
            '*.zhangzishi.cc',
            '*.zhihu.com',
            '*.zhimg.com',
            '*.zhuihd.com',
            '*.bilibili.com',
            'download.jetbrains.com',
            'images-cn.ssl-images-amazon.com',
            'cdn.angruo.com',
            'upos-hz-mirrorakam.akamaized.net',
            'cache.video.iqiyi.com',
            '*.local',
            '192.168.0.0/16',
            '10.0.0.0/8',
            '172.16.0.0/12',
            '127.0.0.0/8',
            '100.64.0.0/10',
            '119.28.28.28/32',
            '17.0.0.0/8',
        ];
    }
}

interface ProxyConfig {
    host: string;
    port: number;
    username: string;
    password: string;
    mode: 'global' | 'rules';
    bypassList?: string[];
}
